import type { JSX } from "react";
import type { ArticleDetail, ArticleListItem } from "@/hooks/useArticles";
import type { ContactSettings } from "@/hooks/useContactSettings";
import type { DepartmentDetail, DepartmentListItem } from "@/hooks/useDepartments";
import type { Doctor, DoctorDetail } from "@/hooks/useDoctors";
import type { HealthPackage } from "@/hooks/usePackages";
import { SITE_URL, getPageUrl } from "@/lib/site";
import { doctorUrl } from "@/lib/slugs";

// Builders for the schema.org JSON-LD emitted from each route's `head()`, so it
// is part of the server-rendered HTML. Only state what the page or CMS actually
// provides: no placeholder images, default languages or empty ratings.

type LdNode = Record<string, unknown>;
type Crumb = [name: string, path: string];

const HOSPITAL_ID = `${SITE_URL}/#hospital`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const HOSPITAL_NAME = "Kathmandu National Medical College & Teaching Hospital";
const DIRECTIONS_URL = "https://maps.app.goo.gl/HVUdGdVyUJmKZK4W9";

function plainText(html: string | null | undefined, maxLength?: number): string | undefined {
  const text = (html ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return undefined;
  if (!maxLength || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).replace(/\s+\S*$/, "")}…`;
}

function nonEmpty<T>(items: T[]): T[] | undefined {
  return items.length ? items : undefined;
}

// Normalizes a CMS value that may be an array or a newline/comma separated string.
export function toList(val: string | string[] | undefined | null): string[] {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  return val
    .split(/\n|,/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// "01-4771556" -> "+977-1-4771556". Only the first line of a multi-line CMS value is used.
function internationalPhone(phone: string | null | undefined): string | undefined {
  const first = phone?.split("\n")[0].trim();
  if (!first) return undefined;
  return first.startsWith("0") ? `+977-${first.slice(1)}` : first;
}

function hospitalRef(): LdNode {
  return { "@type": "Hospital", "@id": HOSPITAL_ID, name: HOSPITAL_NAME, url: getPageUrl("/") };
}

function hospitalNode(contact: ContactSettings | null): LdNode {
  const base: LdNode = {
    ...hospitalRef(),
    alternateName: "KNMC",
    logo: getPageUrl("/logo.png"),
    image: getPageUrl("/logo.png"),
    description:
      "A multi-speciality teaching hospital and medical college in Kathmandu, Nepal, with 24/7 emergency care.",
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
  };
  if (!contact) return base;

  const { address } = contact;
  const emergency = internationalPhone(contact.emergency_phone);
  return {
    ...base,
    telephone: internationalPhone(contact.phone),
    email: contact.email_id || undefined,
    address: address
      ? {
          "@type": "PostalAddress",
          streetAddress:
            [address.line1, address.line2]
              .map((l) => l?.trim())
              .filter(Boolean)
              .join(", ") || undefined,
          addressLocality: address.city?.trim() || undefined,
          addressRegion: address.state?.trim() || undefined,
          postalCode: address.pincode?.trim() || undefined,
          addressCountry: address.country?.trim() || undefined,
        }
      : undefined,
    contactPoint: emergency
      ? { "@type": "ContactPoint", contactType: "emergency", telephone: emergency }
      : undefined,
    hasMap: contact.map_direction_url || DIRECTIONS_URL,
    sameAs: nonEmpty(
      (contact.social_links ?? []).filter((l) => l.enabled && l.url).map((l) => l.url),
    ),
  };
}

function websiteNode(): LdNode {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: getPageUrl("/"),
    name: HOSPITAL_NAME,
    alternateName: "KNMC",
    publisher: { "@id": HOSPITAL_ID },
    inLanguage: "en",
  };
}

function webPage(
  type: string,
  path: string,
  name: string,
  description: string | undefined,
  extra: LdNode = {},
): LdNode {
  const url = getPageUrl(path);
  return {
    "@type": type,
    "@id": `${url}#webpage`,
    url,
    name,
    description,
    isPartOf: { "@id": WEBSITE_ID },
    inLanguage: "en",
    ...extra,
  };
}

function breadcrumbs(crumbs: Crumb[]): LdNode {
  return {
    "@type": "BreadcrumbList",
    itemListElement: [["Home", "/"] as Crumb, ...crumbs].map(([name, path], i) => ({
      "@type": "ListItem",
      position: i + 1,
      name,
      item: getPageUrl(path),
    })),
  };
}

function itemList(items: { name: string; path: string }[]): LdNode {
  return {
    "@type": "ItemList",
    numberOfItems: items.length,
    itemListElement: items.map(({ name, path }, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name,
      url: getPageUrl(path),
    })),
  };
}

// A `head().meta` entry that TanStack Router renders as an HTML-escaped
// <script type="application/ld+json">. The router supports this key at runtime,
// but `head()`'s return type only allows <meta> props, hence the cast.
function ldJson(nodes: LdNode[], hospital: LdNode = hospitalRef()) {
  const entry = {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@graph": [hospital, websiteNode(), ...nodes],
    },
  };
  return entry as unknown as JSX.IntrinsicElements["meta"];
}

export function homeJsonLd(contact: ContactSettings | null) {
  return ldJson(
    [
      webPage(
        "WebPage",
        "/",
        "KNMC — Best Hospital in Kathmandu, Nepal",
        "KNMC is a leading multi-speciality hospital in Kathmandu, Nepal, offering 24/7 emergency care and online appointment booking.",
        { about: { "@id": HOSPITAL_ID } },
      ),
    ],
    hospitalNode(contact),
  );
}

export function aboutJsonLd() {
  return ldJson([
    webPage(
      "AboutPage",
      "/about",
      "About KNMC",
      "Learn about KNMC, a leading teaching hospital and medical college in Kathmandu, Nepal.",
      { about: { "@id": HOSPITAL_ID } },
    ),
    breadcrumbs([["About", "/about"]]),
  ]);
}

export function contactJsonLd(contact: ContactSettings | null) {
  return ldJson(
    [
      webPage(
        "ContactPage",
        "/contact",
        "Contact KNMC Hospital",
        "Address, phone, email, 24/7 emergency line and directions for KNMC in Kathmandu, Nepal.",
        { about: { "@id": HOSPITAL_ID } },
      ),
      breadcrumbs([["Contact", "/contact"]]),
    ],
    hospitalNode(contact),
  );
}

export function doctorsListJsonLd(doctors: Doctor[]) {
  return ldJson([
    webPage(
      "CollectionPage",
      "/doctors",
      "Find a Doctor at KNMC",
      "Doctors and specialists at KNMC Hospital in Kathmandu, Nepal.",
      {
        mainEntity: itemList(
          doctors.map((d) => ({
            name: d.practitioner_name,
            path: doctorUrl(d.department, d.practitioner_name),
          })),
        ),
      },
    ),
    breadcrumbs([["Doctors", "/doctors"]]),
  ]);
}

export function doctorJsonLd(doctor: DoctorDetail, path: string) {
  const url = getPageUrl(path);
  const specialties = toList(doctor.specialties);
  // The CMS can store languages as a single comma-separated array entry.
  const languages = toList(doctor.languages).flatMap((l) => toList(l));
  const awards = (doctor.awards ?? [])
    .map((a) => (typeof a === "string" ? a : a.award))
    .filter(Boolean);

  return ldJson([
    webPage("ProfilePage", path, doctor.practitioner_name, plainText(doctor.bio, 300), {
      mainEntity: { "@id": `${url}#physician` },
    }),
    {
      "@type": "IndividualPhysician",
      "@id": `${url}#physician`,
      name: doctor.practitioner_name,
      url,
      image: doctor.image ?.trim() ? getPageUrl(doctor.image.trim()) : undefined,
      description: plainText(doctor.bio, 300),
      medicalSpecialty: nonEmpty(specialties) ?? doctor.department,
      hospitalAffiliation: { "@id": HOSPITAL_ID },
      identifier: doctor.nmc_number
        ? {
            "@type": "PropertyValue",
            propertyID: "Nepal Medical Council (NMC)",
            value: doctor.nmc_number,
          }
        : undefined,
      knowsLanguage: nonEmpty(languages),
      hasCredential: nonEmpty(
        toList(doctor.qualifications).map((name) => ({
          "@type": "EducationalOccupationalCredential",
          name,
        })),
      ),
      award: nonEmpty(awards),
    },
    breadcrumbs([
      ["Doctors", "/doctors"],
      [doctor.practitioner_name, path],
    ]),
  ]);
}

export function departmentsListJsonLd(departments: DepartmentListItem[]) {
  return ldJson([
    webPage(
      "CollectionPage",
      "/departments",
      "Medical Departments at KNMC",
      "Medical specialities at KNMC, a hospital in Kathmandu, Nepal.",
      {
        mainEntity: itemList(
          departments.map((d) => ({
            name: d.department,
            path: `/departments/${encodeURIComponent(d.name)}`,
          })),
        ),
      },
    ),
    breadcrumbs([["Departments", "/departments"]]),
  ]);
}

export function departmentJsonLd(department: DepartmentDetail, doctors: Doctor[], path: string) {
  const url = getPageUrl(path);
  const description = plainText(department.description);
  const specialists = doctors.filter((d) => d.department === department.department);
  const faqs = department.faqs ?? [];

  return ldJson([
    webPage("WebPage", path, `${department.department} at KNMC`, description, {
      mainEntity: { "@id": `${url}#department` },
    }),
    {
      "@type": "MedicalOrganization",
      "@id": `${url}#department`,
      name: `${department.department} — ${HOSPITAL_NAME}`,
      url,
      description,
      parentOrganization: { "@id": HOSPITAL_ID },
      medicalSpecialty: department.department,
      knowsAbout: nonEmpty(department.specialties ?? []),
      makesOffer: nonEmpty(
        (department.services ?? []).map((s) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: s.title, description: plainText(s.desc) },
        })),
      ),
      member: nonEmpty(
        specialists.map((d) => ({
          "@type": "IndividualPhysician",
          name: d.practitioner_name,
          url: getPageUrl(doctorUrl(d.department, d.practitioner_name)),
        })),
      ),
    },
    ...(faqs.length
      ? [
          {
            "@type": "FAQPage",
            "@id": `${url}#faq`,
            url,
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: plainText(f.q),
              acceptedAnswer: { "@type": "Answer", text: plainText(f.a) },
            })),
          },
        ]
      : []),
    breadcrumbs([
      ["Departments", "/departments"],
      [department.department, path],
    ]),
  ]);
}

export function articlesListJsonLd(articles: ArticleListItem[]) {
  return ldJson([
    webPage(
      "CollectionPage",
      "/health-articles",
      "Health Articles & Tips from KNMC",
      "Health insights and wellness tips from KNMC in Kathmandu, Nepal.",
      {
        mainEntity: itemList(
          articles.map((a) => ({
            name: a.title.trim(),
            path: `/health-articles/${encodeURIComponent(a.slug)}`,
          })),
        ),
      },
    ),
    breadcrumbs([["Health Articles", "/health-articles"]]),
  ]);
}

export function articleJsonLd(article: ArticleDetail, path: string) {
  const url = getPageUrl(path);
  const title = article.title.trim();
  const author = article.author?.trim();
  const description = plainText(article.excerpt);

  return ldJson([
    webPage("WebPage", path, title, description, { mainEntity: { "@id": `${url}#article` } }),
    {
      "@type": "Article",
      "@id": `${url}#article`,
      headline: title,
      description,
      image: article.cover ? getPageUrl(article.cover) : undefined,
      datePublished: article.publish_date || undefined,
      // The author field is free text ("KNMC-hospital", "Health Reporter"), so
      // only a named doctor is credited as a person; the rest are the hospital.
      author:
        author && /^dr\.?\s/i.test(author)
          ? { "@type": "Person", name: author }
          : { "@id": HOSPITAL_ID },
      publisher: { "@id": HOSPITAL_ID },
      mainEntityOfPage: { "@id": `${url}#webpage` },
      articleSection: article.category || undefined,
      keywords: article.meta_keywords || undefined,
      inLanguage: "en",
    },
    breadcrumbs([
      ["Health Articles", "/health-articles"],
      [title, path],
    ]),
  ]);
}

export function packagesJsonLd(packages: HealthPackage[]) {
  const path = "/health-packages";
  return ldJson([
    webPage(
      "CollectionPage",
      path,
      "Health Checkup Packages at KNMC",
      "Health checkup and screening packages at KNMC Hospital in Kathmandu, Nepal.",
      {
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: packages.length,
          itemListElement: packages.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Service",
              name: p.name,
              description: plainText(p.description),
              serviceType: "Health checkup",
              provider: { "@id": HOSPITAL_ID },
              url: getPageUrl(path),
              // A null price is shown as "Not Disclosed", so it gets no Offer.
              offers:
                p.price === null || p.price === undefined
                  ? undefined
                  : {
                      "@type": "Offer",
                      price: Number(p.price),
                      priceCurrency: "NPR",
                      url: getPageUrl(path),
                    },
              hasOfferCatalog: p.items?.length
                ? {
                    "@type": "OfferCatalog",
                    name: `${p.name} tests`,
                    itemListElement: p.items.map((item) => ({
                      "@type": "Offer",
                      itemOffered: { "@type": "Service", name: item.description || item.item_code },
                    })),
                  }
                : undefined,
            },
          })),
        },
      },
    ),
    breadcrumbs([["Health Packages", path]]),
  ]);
}
